/**
 * Map icons (docs/UI.md, *Map icons*): a small painted pictogram per TYPE of place or person with work — a
 * revolver for Sid's, a spray gun for the respray, a pint for Mags' pub — drawn in ink inside the marker's
 * own disc, so the scheme's colours (paper = places, green = work, mustard = offers) still read.
 *
 * One atlas per use (scripts/build-icons.mjs from art/icons/sheet-*.png): atlas-64 for the full map,
 * atlas-128 for the key and the book, atlas-bold-96 (strokes thickened) for the minimap's tiny discs. An atlas
 * cell is the disc's whole diameter (the padding and the weight-normalised size are baked in). Loaded once,
 * lazily; until it has loaded — or if it never does — every marker keeps its glyph letter (the fallback).
 *
 * `iconFor` is pure and unit-tested; the rest touches the DOM.
 */
import type { MarkerKind } from './uiTypes';
import './mapIcons.css';

/** Atlas order (row-major, ICON_COLS wide). Keep in step with SHEETS in scripts/build-icons.mjs. */
export const ICON_IDS = [
  'gun', 'spanner', 'spray', 'lockup', 'house', 'lamp', 'bank', 'works', 'crate', 'scales', 'cash', 'anchor',
  'pint', 'phonebox', 'key', 'flag', 'cab', 'cog', 'brick', 'car', 'fists', 'sportscar', 'door', 'helmet',
] as const;
export type IconId = (typeof ICON_IDS)[number];
export const ICON_COLS = 6;
const ICON_ROWS = Math.ceil(ICON_IDS.length / ICON_COLS);
/** Pixels per icon in the canvas atlas, and in the minimap's bold one. */
export const ICON_CELL = 64;
export const ICON_CELL_BOLD = 96;

/** Places and shops (`poi`), by the glyph the marker carries. */
const POI_ICON: Record<string, IconId> = {
  F: 'gun', // Sid's back room: the weapons
  G: 'spanner', // garage: repairs
  R: 'spray', // respray
  L: 'lockup',
  S: 'house', // safehouse
  '!': 'helmet', // police station
  '£': 'cash', // dropped cash
  P: 'works', // your premises (online)
  B: 'bank',
  N: 'crate', // the night freight pier
  T: 'scales', // Wilf's trade: buy and sell
};
/** People with work (`friend`). */
const FRIEND_ICON: Record<string, IconId> = {
  M: 'pint', // Mags, the Crooked Anchor's back room
  '☎': 'phonebox',
  '£': 'key', // Halloran's export orders
  '★': 'flag', // Rita's races
  C: 'cab', // the minicab firm
  W: 'cog', // Des's workshop
  H: 'brick', // half-brick mayhem
  '?': 'car', // a car with a job in it
};
/** Online happenings and offers (`event`). */
const EVENT_ICON: Record<string, IconId> = { '£': 'cash', X: 'fists', '⚓': 'anchor', '★': 'sportscar', Y: 'works' };

/** The icon for a marker (kind + glyph), or null: the marker keeps its glyph (or shape). */
export function iconFor(kind: MarkerKind | 'you', glyph: string | undefined): IconId | null {
  if (!glyph) return null;
  const table = kind === 'poi' ? POI_ICON : kind === 'friend' ? FRIEND_ICON : kind === 'event' ? EVENT_ICON : null;
  return table?.[glyph] ?? null;
}

/** Where an icon sits in the atlas, in cells. */
export function iconCell(id: IconId): { col: number; row: number } {
  const i = ICON_IDS.indexOf(id);
  return { col: i % ICON_COLS, row: Math.floor(i / ICON_COLS) };
}

const BASE = import.meta.env?.BASE_URL ?? './';
export const ICON_ATLAS_URL = `${BASE}art/icons/atlas-64.webp`;
export const ICON_ATLAS_BIG_URL = `${BASE}art/icons/atlas-128.webp`;
export const ICON_ATLAS_BOLD_URL = `${BASE}art/icons/atlas-bold-96.webp`;

let atlas: HTMLImageElement | null = null;
let ready = false;
let boldAtlas: HTMLImageElement | null = null;
let boldReady = false;

/** Start loading the atlases (once). When the big one is in, `html.bh-icons` turns the CSS icons on. */
export function loadMapIcons(): void {
  if (atlas || typeof Image === 'undefined') return;
  atlas = new Image();
  atlas.decoding = 'async';
  atlas.onload = () => {
    ready = true;
  };
  atlas.src = ICON_ATLAS_URL;
  boldAtlas = new Image();
  boldAtlas.decoding = 'async';
  boldAtlas.onload = () => {
    boldReady = true;
  };
  boldAtlas.src = ICON_ATLAS_BOLD_URL;
  const big = new Image();
  big.onload = () => document.documentElement.classList.add('bh-icons');
  big.src = ICON_ATLAS_BIG_URL;
}

/** The canvas atlas has loaded (markers with an icon draw a size bigger from then on). */
export function mapIconsReady(): boolean {
  return ready;
}

/**
 * Draw an icon into the disc centred on (x, y), `size` px = the disc's diameter, from the canvas atlas (the
 * `bold` one for the minimap, falling back to the plain one). False while no atlas is there (the caller
 * draws the glyph instead).
 */
export function drawMapIcon(g: CanvasRenderingContext2D, id: IconId, x: number, y: number, size: number, bold = false): boolean {
  const useBold = bold && boldReady && !!boldAtlas;
  const img = useBold ? boldAtlas : atlas;
  if (!img || !(useBold || ready)) return false;
  const cell = useBold ? ICON_CELL_BOLD : ICON_CELL;
  const { col, row } = iconCell(id);
  g.drawImage(img, col * cell, row * cell, cell, cell, x - size / 2, y - size / 2, size, size);
  return true;
}

/**
 * Show icon `id` on a DOM element as a background from the big atlas (class `bh-ic`, styled in guide.css).
 * The element's own text (the glyph) stays as the fallback; CSS hides it once `html.bh-icons` is set.
 */
export function applyIcon(elm: HTMLElement, id: IconId): void {
  loadMapIcons();
  const { col, row } = iconCell(id);
  elm.classList.add('bh-ic');
  // Absolute: a url() in a custom property resolves against the stylesheet that uses it (assets/ in a build).
  elm.style.setProperty('--ic-img', `url("${new URL(ICON_ATLAS_BIG_URL, document.baseURI).href}")`);
  elm.style.setProperty('--ic-size', `${ICON_COLS * 100}% ${ICON_ROWS * 100}%`);
  elm.style.setProperty('--ic-pos', `${(col / (ICON_COLS - 1)) * 100}% ${(row / Math.max(1, ICON_ROWS - 1)) * 100}%`);
}
